import { useEffect } from 'react';
import type { RealtimeStore } from './store';

/** Without any input for this long, an "available" person is shown as away. */
export const IDLE_AWAY_MS = 10 * 60_000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Sets the automatic "away" after `idleMs` without input, and takes it back on the next input,
 * which restores the status the person had picked (it is never overwritten). Only an "available"
 * person goes away by themselves: busy, on site and away are deliberate choices.
 */
export function useIdleAway(store: RealtimeStore, active: boolean, idleMs = IDLE_AWAY_MS): void {
  useEffect(() => {
    if (!active) return;
    let lastInput = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = () => {
      const idle = Date.now() - lastInput;
      if (idle >= idleMs) {
        const state = store.getState();
        if (state.chosen === 'available' && !state.autoAway) state.setAutoAway(true);
        timer = setTimeout(check, idleMs);
      } else {
        timer = setTimeout(check, idleMs - idle);
      }
    };
    timer = setTimeout(check, idleMs);

    const onInput = () => {
      const now = Date.now();
      const { autoAway, setAutoAway } = store.getState();
      // Pointer moves come by the dozen: once a second is enough to know the person is there.
      if (now - lastInput < 1_000 && !autoAway) return;
      lastInput = now;
      if (autoAway) setAutoAway(false);
    };
    for (const name of ACTIVITY_EVENTS) {
      document.addEventListener(name, onInput, { passive: true });
    }
    return () => {
      if (timer) clearTimeout(timer);
      for (const name of ACTIVITY_EVENTS) document.removeEventListener(name, onInput);
    };
  }, [store, active, idleMs]);
}
